import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'
import NoImage from 'assets/no-image.jpg'
import { DropdownButton } from 'components'
import Icon from 'components/Icon'
import LocationIndicator from 'components/LocationIndicator'
import { ShowItem } from 'generated/graphql'
import { useDeleteItem, useEventSlug } from 'hooks'
import { timeFormatSelector } from 'features/events/eventSlice'
import { addModal } from 'features/modals/modalSlice'
import { colors, _ } from 'utils'
import CreateUpdateShowContainer from '../CreateUpdateShowContainer'
import routes from '../routes'
import ProgramRow, { formatDate, getLocationType } from './ProgramRow'
import { deleteShowContainer } from '../graphql/mutation'

export const S = {
    Wrapper: styled.div<{ color?: string, parentColor?: string }>`
        display: grid;
        grid-template-columns: 60px minmax(150px, 250px) minmax(200px, 1fr) 130px 80px;
        grid-column-gap: 8px;
        align-items: center;
        height: 56px;
        width: 100%;
        padding: 8px;
        background-color: ${colors.pageBackground};
        margin-bottom: 3px;
        border-right: ${({ color }) => (color ? `4px solid ${color}` : 'none')};
        &:hover .program-row-actions {
            display: flex !important;
        }
        ${({ parentColor }) => parentColor && css`
            border-radius: 5px;
            border: 1px solid ${parentColor};
        `}
    `,
    Image: styled.div<{ image: string }>`
        width: 56px;
        height: 40px;
        background: ${({ image }) => (image ? `url(${image})` : 'transparent')};
        background-size: cover;
        background-position: center;
        border-radius: 2px;
    `,
    Title: styled.span<{ color?: string }>`
        position: relative;
        padding-left: ${({ color }) => (!color ? 4 : 21)}px;
        margin-bottom: ${({ color }) => (!color ? 4 : 0)}px;
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        color: #4F555F;
        &:after {
            content: "";
            position: absolute;
            top: 5px;
            left: 3px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: ${({ color }) => color || 'transparent'}
        }
    `,
    Location: styled.div`
        display: flex;
        height: 16px;
        align-items: center;
        color: ${colors.icon};
        font-size: 14px;
        line-height: 16px;
        & > span {
            margin-right: 4px;
        }
    `,
    Description: styled.div`
        font-size: 14px;
        color: ${colors.grayTitleOpacity(0.8)};
    `,
    Actions: styled.div`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        & .program-row-actions {
            display: none;

        }
    `,
    DownArrow: styled.div<{ isExpanding: boolean }>`
        display: flex;
        align-items: center;
        font-size: 14px;
        color: ${colors.grayDescription};
        & > div {
            margin-bottom: -3px;
            margin-right: -3px;
        }
        & > span {
            margin-bottom: ${({ isExpanding }) => (isExpanding ? -6 : 0)}px;
            transform: rotate(${({ isExpanding }) => (isExpanding ? 180 : 0)}deg);
            transition: 0.3s;
        }
    `,
    ChildItems: styled.div<{ maxHeight: number }>`
        max-height: ${({ maxHeight }) => maxHeight}px;
        padding-left: 20px;
        overflow: ${({ maxHeight }) => (maxHeight ? 'visible' : 'hidden')};
        transition: 0.3s;
    `
}

const MoreOptionsButton = () => <Icon icon="dotsVertical" size={20} onClick={() => null} />

interface Props {
    row: ShowItem
    fetchData?: () => void
}

const ProgramContainerRow = ({ row, fetchData }: Props) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const eventSlug = useEventSlug()
    const timeFormat = useSelector(timeFormatSelector)
    const rowSpaceName = row?.virtualSpace || row?.mapitem
    const [isExpanding, setIsExpanding] = useState(false)

    const deleteShowContainerFn = () => deleteShowContainer({ id: row.id })
    const { showDeleteConfirmModal } = useDeleteItem(deleteShowContainerFn, fetchData)

    const showProgramContainerModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CreateUpdateShowContainer close={close} id={row.id} callGetShows={fetchData} />
            ),
            header: _('show_container.modal_header.update'),
        }))
    }

    const createChildProgram = () => {
        const pathname = routes.createShow.getPath({ eventSlug })
        const state = { id: row.id, startDate: row.startDate, endDate: row.endDate }
        history.push({ pathname, state })
    }

    return (
        <>
            <S.Wrapper color={row.isContainer ? row.color : ''} onClick={() => setIsExpanding(!isExpanding)}>
                <S.Image image={row.virtualImage || row.image || NoImage} />
                <div style={{ overflow: 'hidden' }}>
                    <S.Title className="text-cut">
                        {row.name}
                    </S.Title>

                    <S.Location>
                        <Icon icon="calendarClock" size={14} />
                        {row.startDate && formatDate(
                            new Date(row.startDate), new Date(row.endDate), timeFormat
                        )}
                    </S.Location>
                </div>
                <div style={{ minWidth: 0 }}>
                    <S.Description className="text-cut">
                        {row.description}
                    </S.Description>
                    <LocationIndicator location={getLocationType(row)} />
                </div>
                <div>
                    {rowSpaceName && (
                        <S.Location style={{ height: 36 }}>
                            {rowSpaceName ? (
                                <>
                                    <Icon icon="mapMarker" size={16} className="d-flex" />
                                    <div className="text-cut-line-2">
                                        {rowSpaceName}
                                    </div>
                                </>
                            ) : <div className="ml-1">{_('show.list.item.no_space')}</div>}
                        </S.Location>
                    )}
                </div>
                <S.Actions>
                    <div className="program-row-actions">
                        <DropdownButton
                            renderToggleButton={MoreOptionsButton}
                        >
                            <div onClick={showProgramContainerModal}>{_('button.edit')}</div>
                            <div onClick={showDeleteConfirmModal}>{_('button.delete')}</div>
                        </DropdownButton>
                        <Icon icon="plus" style={{ marginRight: 4 }} size={24} onClick={createChildProgram} />
                    </div>
                    <S.DownArrow isExpanding={isExpanding}>
                        <div>
                            {row.items?.length || 0}
                        </div>
                        <Icon icon="chevronDown" size={18} />
                    </S.DownArrow>
                </S.Actions>
            </S.Wrapper>
            <S.ChildItems
                maxHeight={(isExpanding && (row?.items || []).length * 65) || 0}
            >
                {(row?.items || []).map(
                    (item) => (
                        <ProgramRow row={item} key={item.id} fetchData={fetchData} parentColor={row.color} />
                    )
                )}
            </S.ChildItems>
        </>
    )
}

export default ProgramContainerRow
